import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

interface BugItem {
  _id: string;
  bugKey: string;
  summary: string;
  assignTo: Array<{
    _id: string;
    fullName: string;
  }>;
  bugStatus: {
    _id: string;
    statusCode: string;
    status: string;
  };
  severity: string;
  type: string;
  createdBy: {
    _id: string;
    fullName: string;
  };
  project: {
    _id: string;
    name: string;
    code: string;
  };
  createdAt: string;
  updatedAt: string;
  subTasks?: Array<{
    _id: string;
    [key: string]: any;
  }>;
}

interface Sprint {
  _id: string;
  name: string;
  status: string;
  startDate?: string;
  endDate?: string;
  goal?: string;
}

interface UseDragAndDropProps {
  backlogData: any;
  sprintTasksData: { [key: string]: any };
  setSprintTasksData: React.Dispatch<
    React.SetStateAction<{ [key: string]: any }>
  >;
  updateBugMutation: any; // Single task update
  updateSprintStatusMutation: any; // Multi task update
  refetchBugList: () => void;
  sprints: Sprint[];
  containerSelections: { [key: string]: Set<string> };
  setContainerSelections: React.Dispatch<
    React.SetStateAction<{ [key: string]: Set<string> }>
  >;
}

export const useDragAndDrop = ({
  backlogData,
  sprintTasksData,
  setSprintTasksData,
  updateBugMutation,
  updateSprintStatusMutation,
  refetchBugList,
  sprints,
  containerSelections,
  setContainerSelections,
}: UseDragAndDropProps) => {
  const { t } = useTranslation(["bugherd"]);

  // Drag states
  const [activeTask, setActiveTask] = useState<BugItem | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedTasks, setDraggedTasks] = useState<BugItem[]>([]);

  // Get active container (container with selections)
  const getActiveContainer = useCallback((): string | null => {
    for (const [containerId, selections] of Object.entries(
      containerSelections
    )) {
      if (selections.size > 0) {
        return containerId;
      }
    }
    return null;
  }, [containerSelections]);

  // Get all selected tasks across containers
  const getAllSelectedTasks = useCallback((): Set<string> => {
    const allSelected = new Set<string>();
    Object.values(containerSelections).forEach((containerSelection) => {
      containerSelection.forEach((taskId) => allSelected.add(taskId));
    });
    return allSelected;
  }, [containerSelections]);

  // Clear all selections
  const clearAllSelections = useCallback(() => {
    setContainerSelections({});
  }, [setContainerSelections]);

  // Clear selections from specific container
  const clearContainerSelections = useCallback(
    (containerId: string) => {
      setContainerSelections((prev) => {
        const updated = { ...prev };
        delete updated[containerId];
        return updated;
      });
    },
    [setContainerSelections]
  );

  // Find task in any container
  const findTask = useCallback(
    (taskId: string): BugItem | null => {
      // Check in backlog
      const backlogTasks = backlogData?.data?.decryptedData?.items || [];
      let task = backlogTasks.find((task: BugItem) => task._id === taskId);

      if (!task) {
        // Check in sprint tasks
        Object.values(sprintTasksData).forEach((sprintData: any) => {
          const sprintTasks = sprintData?.data?.decryptedData?.items || [];
          const foundTask = sprintTasks.find(
            (task: BugItem) => task._id === taskId
          );
          if (foundTask) {
            task = foundTask;
          }
        });
      }

      return task || null;
    },
    [backlogData, sprintTasksData]
  );

  // Find which container a task belongs to
  const findTaskContainer = useCallback(
    (taskId: string): string | null => {
      // Check in backlog
      const backlogTasks = backlogData?.data?.decryptedData?.items || [];
      if (backlogTasks.some((task: BugItem) => task._id === taskId)) {
        return "backlog";
      }

      // Check in sprint tasks
      for (const sprintId in sprintTasksData) {
        const sprintTasks =
          sprintTasksData[sprintId]?.data?.decryptedData?.items || [];
        if (sprintTasks.some((task: BugItem) => task._id === taskId)) {
          return `sprint-${sprintId}`;
        }
      }

      return null;
    },
    [backlogData, sprintTasksData]
  );

  // Get sprint by container ID
  const getSprintByContainerId = useCallback(
    (containerId: string): Sprint | null => {
      if (containerId === "backlog") return null;
      const sprintId = containerId.replace("sprint-", "");
      return sprints.find((sprint) => sprint._id === sprintId) || null;
    },
    [sprints]
  );

  // NEW: Extract task ID and all subtask IDs
  const getTaskWithSubtaskIds = useCallback((task: BugItem): string[] => {
    const ids = [task._id];

    // Add subtask IDs if they exist
    if (
      task.subTasks &&
      Array.isArray(task.subTasks) &&
      task.subTasks.length > 0
    ) {
      const subtaskIds = task.subTasks.map((subtask) => subtask._id);
      ids.push(...subtaskIds);
    }

    return ids;
  }, []);

  // NEW: Get all task IDs including subtasks for multiple tasks
  const getAllTaskIdsWithSubtasks = useCallback(
    (tasks: BugItem[]): string[] => {
      const allIds: string[] = [];

      tasks.forEach((task) => {
        const idsForTask = getTaskWithSubtaskIds(task);
        allIds.push(...idsForTask);
      });

      return allIds;
    },
    [getTaskWithSubtaskIds]
  );

  // Update local state immediately for single task
  const updateLocalStateSingle = useCallback(
    (
      taskId: string,
      fromContainer: string,
      toContainer: string,
      task: BugItem
    ) => {
      if (fromContainer === "backlog" && toContainer.startsWith("sprint-")) {
        // Moving from backlog to sprint
        const targetSprintId = toContainer.replace("sprint-", "");

        setSprintTasksData((prev) => {
          const currentSprintData = prev[targetSprintId] || {
            data: { decryptedData: { items: [], total: 0 } },
          };
          const currentItems =
            currentSprintData.data?.decryptedData?.items || [];

          // Check if task already exists to avoid duplicates
          if (currentItems.some((item: BugItem) => item._id === taskId)) {
            return prev;
          }

          return {
            ...prev,
            [targetSprintId]: {
              ...currentSprintData,
              data: {
                ...currentSprintData.data,
                decryptedData: {
                  ...currentSprintData.data.decryptedData,
                  items: [...currentItems, task],
                  total:
                    (currentSprintData.data?.decryptedData?.total || 0) + 1,
                },
              },
            },
          };
        });
      } else if (
        fromContainer.startsWith("sprint-") &&
        toContainer === "backlog"
      ) {
        // Moving from sprint to backlog
        const sourceSprintId = fromContainer.replace("sprint-", "");

        setSprintTasksData((prev) => {
          const currentSprintData = prev[sourceSprintId];
          if (!currentSprintData) return prev;

          const currentItems =
            currentSprintData.data?.decryptedData?.items || [];
          const filteredItems = currentItems.filter(
            (item: BugItem) => item._id !== taskId
          );

          return {
            ...prev,
            [sourceSprintId]: {
              ...currentSprintData,
              data: {
                ...currentSprintData.data,
                decryptedData: {
                  ...currentSprintData.data.decryptedData,
                  items: filteredItems,
                  total: Math.max(
                    0,
                    (currentSprintData.data?.decryptedData?.total || 0) - 1
                  ),
                },
              },
            },
          };
        });
      } else if (
        fromContainer.startsWith("sprint-") &&
        toContainer.startsWith("sprint-")
      ) {
        // Moving between sprints
        const sourceSprintId = fromContainer.replace("sprint-", "");
        const targetSprintId = toContainer.replace("sprint-", "");

        setSprintTasksData((prev) => {
          const sourceSprintData = prev[sourceSprintId];
          const targetSprintData = prev[targetSprintId] || {
            data: { decryptedData: { items: [], total: 0 } },
          };

          if (!sourceSprintData) return prev;

          // Remove from source
          const sourceItems = sourceSprintData.data?.decryptedData?.items || [];
          const filteredSourceItems = sourceItems.filter(
            (item: BugItem) => item._id !== taskId
          );

          // Add to target
          const targetItems = targetSprintData.data?.decryptedData?.items || [];
          const updatedTargetItems = targetItems.some(
            (item: BugItem) => item._id === taskId
          )
            ? targetItems
            : [...targetItems, task];

          return {
            ...prev,
            [sourceSprintId]: {
              ...sourceSprintData,
              data: {
                ...sourceSprintData.data,
                decryptedData: {
                  ...sourceSprintData.data.decryptedData,
                  items: filteredSourceItems,
                  total: Math.max(
                    0,
                    (sourceSprintData.data?.decryptedData?.total || 0) - 1
                  ),
                },
              },
            },
            [targetSprintId]: {
              ...targetSprintData,
              data: {
                ...targetSprintData.data,
                decryptedData: {
                  ...targetSprintData.data.decryptedData,
                  items: updatedTargetItems,
                  total: targetItems.some(
                    (item: BugItem) => item._id === taskId
                  )
                    ? targetSprintData.data?.decryptedData?.total || 0
                    : (targetSprintData.data?.decryptedData?.total || 0) + 1,
                },
              },
            },
          };
        });
      }
    },
    [setSprintTasksData]
  );

  // Update local state immediately for multiple tasks
  const updateLocalStateMultiple = useCallback(
    (
      taskIds: string[],
      fromContainer: string,
      toContainer: string,
      tasks: BugItem[]
    ) => {
      if (fromContainer === "backlog" && toContainer.startsWith("sprint-")) {
        // Moving from backlog to sprint
        const targetSprintId = toContainer.replace("sprint-", "");

        setSprintTasksData((prev) => {
          const currentSprintData = prev[targetSprintId] || {
            data: { decryptedData: { items: [], total: 0 } },
          };
          const currentItems =
            currentSprintData.data?.decryptedData?.items || [];

          // Filter out tasks that already exist to avoid duplicates
          const existingTaskIds = currentItems.map((item: BugItem) => item._id);
          const newTasks = tasks.filter(
            (task) => !existingTaskIds.includes(task._id)
          );

          if (newTasks.length === 0) return prev;

          return {
            ...prev,
            [targetSprintId]: {
              ...currentSprintData,
              data: {
                ...currentSprintData.data,
                decryptedData: {
                  ...currentSprintData.data.decryptedData,
                  items: [...currentItems, ...newTasks],
                  total:
                    (currentSprintData.data?.decryptedData?.total || 0) +
                    newTasks.length,
                },
              },
            },
          };
        });
      } else if (
        fromContainer.startsWith("sprint-") &&
        toContainer === "backlog"
      ) {
        // Moving from sprint to backlog
        const sourceSprintId = fromContainer.replace("sprint-", "");

        setSprintTasksData((prev) => {
          const currentSprintData = prev[sourceSprintId];
          if (!currentSprintData) return prev;

          const currentItems =
            currentSprintData.data?.decryptedData?.items || [];
          const filteredItems = currentItems.filter(
            (item: BugItem) => !taskIds.includes(item._id)
          );

          return {
            ...prev,
            [sourceSprintId]: {
              ...currentSprintData,
              data: {
                ...currentSprintData.data,
                decryptedData: {
                  ...currentSprintData.data.decryptedData,
                  items: filteredItems,
                  total: Math.max(
                    0,
                    (currentSprintData.data?.decryptedData?.total || 0) -
                      taskIds.length
                  ),
                },
              },
            },
          };
        });
      } else if (
        fromContainer.startsWith("sprint-") &&
        toContainer.startsWith("sprint-")
      ) {
        // Moving between sprints
        const sourceSprintId = fromContainer.replace("sprint-", "");
        const targetSprintId = toContainer.replace("sprint-", "");

        setSprintTasksData((prev) => {
          const sourceSprintData = prev[sourceSprintId];
          const targetSprintData = prev[targetSprintId] || {
            data: { decryptedData: { items: [], total: 0 } },
          };

          if (!sourceSprintData) return prev;

          // Remove from source
          const sourceItems = sourceSprintData.data?.decryptedData?.items || [];
          const filteredSourceItems = sourceItems.filter(
            (item: BugItem) => !taskIds.includes(item._id)
          );

          // Add to target
          const targetItems = targetSprintData.data?.decryptedData?.items || [];
          const existingTargetTaskIds = targetItems.map(
            (item: BugItem) => item._id
          );
          const newTasks = tasks.filter(
            (task) => !existingTargetTaskIds.includes(task._id)
          );

          return {
            ...prev,
            [sourceSprintId]: {
              ...sourceSprintData,
              data: {
                ...sourceSprintData.data,
                decryptedData: {
                  ...sourceSprintData.data.decryptedData,
                  items: filteredSourceItems,
                  total: Math.max(
                    0,
                    (sourceSprintData.data?.decryptedData?.total || 0) -
                      taskIds.length
                  ),
                },
              },
            },
            [targetSprintId]: {
              ...targetSprintData,
              data: {
                ...targetSprintData.data,
                decryptedData: {
                  ...targetSprintData.data.decryptedData,
                  items: [...targetItems, ...newTasks],
                  total:
                    (targetSprintData.data?.decryptedData?.total || 0) +
                    newTasks.length,
                },
              },
            },
          };
        });
      }
    },
    [setSprintTasksData]
  );

  // Handle single task move - UPDATED to include subtasks
  const handleSingleTaskMove = useCallback(
    async (
      taskId: string,
      fromContainer: string,
      toContainer: string,
      task: BugItem
    ) => {
      // 1. IMMEDIATE LOCAL STATE UPDATE
      updateLocalStateSingle(taskId, fromContainer, toContainer, task);

      // Get all IDs including subtasks
      const allTaskIds = getTaskWithSubtaskIds(task);
      // Determine the API payload
      let apiPayload: any = {
        bugId: taskId,
      };

      if (fromContainer === "backlog" && toContainer.startsWith("sprint-")) {
        // Moving from backlog to sprint
        const sprintId = toContainer.replace("sprint-", "");
        apiPayload = {
          ...apiPayload,
          currentSprint: sprintId,
          isBackLog: false,
        };
      } else if (
        fromContainer.startsWith("sprint-") &&
        toContainer === "backlog"
      ) {
        // Moving from sprint to backlog
        apiPayload = {
          ...apiPayload,
          currentSprint: null,
          isBackLog: true,
        };
      } else if (
        fromContainer.startsWith("sprint-") &&
        toContainer.startsWith("sprint-")
      ) {
        // Moving between sprints
        const targetSprintId = toContainer.replace("sprint-", "");
        apiPayload = {
          ...apiPayload,
          currentSprint: targetSprintId,
          isBackLog: false,
        };
      }

      try {
        // 2. BACKGROUND API CALL
        await updateBugMutation(apiPayload).unwrap();

        // Show appropriate success message
        const subtaskCount = allTaskIds.length - 1;
        if (subtaskCount > 0) {
          toast.success(
            t("Task moved successfully with {{count}} subtask(s)", {
              count: subtaskCount,
            })
          );
        } else {
          toast.success(t("Task moved successfully"));
        }

        // 3. Clear selections from source container
        clearContainerSelections(fromContainer);

        // 4. Refresh backlog data only when moving to/from backlog
        if (fromContainer === "backlog" || toContainer === "backlog") {
          refetchBugList();
        }
      } catch (error) {
        console.error("Error moving task:", error);
        toast.error(t("Failed to move task"));

        // 5. REVERT LOCAL STATE ON ERROR
        updateLocalStateSingle(taskId, toContainer, fromContainer, task);

        // Refresh data to ensure consistency
        if (fromContainer === "backlog" || toContainer === "backlog") {
          refetchBugList();
        }
      }
    },
    [
      updateLocalStateSingle,
      getTaskWithSubtaskIds,
      updateBugMutation,
      t,
      clearContainerSelections,
      refetchBugList,
    ]
  );

  // Handle multiple tasks move - UPDATED to include subtasks
  const handleMultipleTasksMove = useCallback(
    async (
      taskIds: string[],
      fromContainer: string,
      toContainer: string,
      tasks: BugItem[]
    ) => {
      // 1. IMMEDIATE LOCAL STATE UPDATE
      updateLocalStateMultiple(taskIds, fromContainer, toContainer, tasks);

      // Get all IDs including subtasks for all tasks
      const allTaskIdsWithSubtasks = getAllTaskIdsWithSubtasks(tasks);

      // Determine the API payload based on move type
      let apiPayload: any = {};

      if (fromContainer === "backlog" && toContainer.startsWith("sprint-")) {
        // Moving from backlog to sprint
        const targetSprint = getSprintByContainerId(toContainer);
        if (!targetSprint) {
          console.error("Target sprint not found");
          return;
        }

        apiPayload = {
          status: targetSprint.status,
          sprintId: targetSprint._id,
          bugs: allTaskIdsWithSubtasks,
        };
      } else if (
        fromContainer.startsWith("sprint-") &&
        toContainer === "backlog"
      ) {
        // Moving from sprint to backlog
        const sourceSprint = getSprintByContainerId(fromContainer);
        if (!sourceSprint) {
          console.error("Source sprint not found");
          return;
        }

        apiPayload = {
          status: sourceSprint.status,
          sprintId: sourceSprint._id,
          bugs: allTaskIdsWithSubtasks,
          moveItems: "BACKLOG",
        };
      } else if (
        fromContainer.startsWith("sprint-") &&
        toContainer.startsWith("sprint-")
      ) {
        // Moving between sprints
        const targetSprint = getSprintByContainerId(toContainer);
        if (!targetSprint) {
          console.error("Target sprint not found");
          return;
        }

        apiPayload = {
          status: targetSprint.status,
          sprintId: targetSprint._id,
          bugs: allTaskIdsWithSubtasks,
        };
      }

      try {
        // 2. BACKGROUND API CALL
        await updateSprintStatusMutation(apiPayload).unwrap();

        // Show appropriate success message
        const taskCount = tasks.length;
        const totalSubtasks = allTaskIdsWithSubtasks.length - taskCount;

        if (totalSubtasks > 0) {
          toast.success(
            taskCount === 1
              ? t("Task moved successfully with {{count}} subtask(s)", {
                  count: totalSubtasks,
                })
              : t(
                  "{{count}} tasks moved successfully with {{subtaskCount}} subtask(s)",
                  {
                    count: taskCount,
                    subtaskCount: totalSubtasks,
                  }
                )
          );
        } else {
          toast.success(
            taskCount === 1
              ? t("Task moved successfully")
              : t("{{count}} tasks moved successfully", { count: taskCount })
          );
        }

        // 3. Clear selections from source container
        clearContainerSelections(fromContainer);

        // 4. Refresh backlog data only when moving to/from backlog
        if (fromContainer === "backlog" || toContainer === "backlog") {
          refetchBugList();
        }
      } catch (error) {
        console.error("Error moving tasks:", error);
        toast.error(t("Failed to move tasks"));

        // 5. REVERT LOCAL STATE ON ERROR
        updateLocalStateMultiple(taskIds, toContainer, fromContainer, tasks);

        // Refresh data to ensure consistency
        if (fromContainer === "backlog" || toContainer === "backlog") {
          refetchBugList();
        }
      }
    },
    [
      updateLocalStateMultiple,
      getAllTaskIdsWithSubtasks,
      getSprintByContainerId,
      updateSprintStatusMutation,
      t,
      clearContainerSelections,
      refetchBugList,
    ]
  );

  // Handle task move with automatic detection of single vs multiple
  const handleTaskMove = useCallback(
    async (taskId: string, fromContainer: string, toContainer: string) => {
      const task = findTask(taskId);
      if (!task) {
        console.error("Task not found:", taskId);
        return;
      }

      // Get selected tasks from the source container
      const sourceContainerSelections =
        containerSelections[fromContainer] || new Set();

      // Determine which tasks to move
      let shouldUseMultiMove = false;
      let tasksToMove: string[] = [];
      let taskObjects: BugItem[] = [];

      if (
        sourceContainerSelections.has(taskId) &&
        sourceContainerSelections.size > 1
      ) {
        // Multi-select mode: move all selected tasks from source container
        shouldUseMultiMove = true;
        tasksToMove = Array.from(sourceContainerSelections);
        taskObjects = tasksToMove
          .map((id) => findTask(id))
          .filter(Boolean) as BugItem[];
      } else {
        // Single task mode
        shouldUseMultiMove = false;
        tasksToMove = [taskId];
        taskObjects = [task];
      }

      if (shouldUseMultiMove) {
        await handleMultipleTasksMove(
          tasksToMove,
          fromContainer,
          toContainer,
          taskObjects
        );
      } else {
        await handleSingleTaskMove(taskId, fromContainer, toContainer, task);
      }
    },
    [
      containerSelections,
      findTask,
      handleMultipleTasksMove,
      handleSingleTaskMove,
    ]
  );

  // Drag event handlers
  const handleDragStart = useCallback(
    (event: any) => {
      const { active } = event;
      const taskId = active.id as string;
      const task = findTask(taskId);

      if (task) {
        const sourceContainer = findTaskContainer(taskId);
        if (!sourceContainer) return;

        const sourceContainerSelections =
          containerSelections[sourceContainer] || new Set();
        let tasksBeingDragged: BugItem[] = [];

        if (
          sourceContainerSelections.has(taskId) &&
          sourceContainerSelections.size > 1
        ) {
          // Multi-select drag: get all selected tasks from source container
          const selectedTaskIds = Array.from(sourceContainerSelections);
          tasksBeingDragged = selectedTaskIds
            .map((id) => findTask(id))
            .filter(Boolean) as BugItem[];
        } else {
          // Single task drag
          tasksBeingDragged = [task];
        }

        setActiveTask(task);
        setDraggedTasks(tasksBeingDragged);
        setIsDragging(true);
      }
    },
    [findTask, findTaskContainer, containerSelections]
  );

  const handleDragOver = useCallback((event: any) => {
    const { active, over } = event;
    if (!over) return;
  }, []);

  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;

      // Reset drag state
      setActiveTask(null);
      setDraggedTasks([]);
      setIsDragging(false);

      if (!over) {
        return;
      }

      const taskId = active.id as string;
      const targetContainerId = over.id as string;
      const sourceContainer = findTaskContainer(taskId);

      if (!sourceContainer) {
        return;
      }

      if (sourceContainer === targetContainerId) {
        return;
      }

      handleTaskMove(taskId, sourceContainer, targetContainerId);
    },
    [findTaskContainer, handleTaskMove]
  );

  return {
    // States
    activeTask,
    isDragging,
    draggedTasks,

    // Handlers
    handleDragStart,
    handleDragOver,
    handleDragEnd,

    // Utilities
    findTask,
    findTaskContainer,
    getActiveContainer,
    getAllSelectedTasks,
    clearAllSelections,
    clearContainerSelections,
    getTaskWithSubtaskIds,
    getAllTaskIdsWithSubtasks,
  };
};
